import { useEffect, useState } from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Swal from "sweetalert2";
import {
  FiGrid,
  FiLayers,
  FiPackage,
  FiPlusSquare,
  FiUser,
  FiShoppingCart,
  FiLogOut,
  FiMessageSquare,
} from "react-icons/fi";
import "./assets/css/App.scss";

import Home, { CategoryHub } from "./pages/Home";
import MyProducts from "./pages/MyProducts";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import SubscriptionDetails from "./pages/SubscriptionDetails";
import Header from "./components/Header";
import TermsAndConditions from "./components/TermsAndConditions";
import Authentication from "./pages/auth/authentication";
import PrivacyPolicy from "./components/PrivacyPolicy";
import AddProduct from "./pages/AddProduct";
import { APP_LOGO } from "./config/AppConfig";
import { CategoryProducts } from "./pages/CategoryProducts";
import ChatInbox from "./pages/ChatInbox";
import ChatRoom from "./pages/ChatRoom";

const dashboardLinks = [
  { id: "dashboard", label: "Dashboard", path: "/", icon: FiGrid },
  { id: "add-product", label: "Add Product", path: "/addProduct", icon: FiPlusSquare },
  { id: "my-product", label: "My Product", path: "/my-products", icon: FiPackage },
  { id: "category", label: "Category", path: "/categories", icon: FiLayers },
  { id: "profile", label: "Profile", path: "/profile", icon: FiUser },
  { id: "chat", label: "Chat Inbox", path: "/chat", icon: FiMessageSquare },
  { id: "subcription", label: "Subcription", path: "/subcription", icon: FiShoppingCart },
  { id: "logout", label: "Logout", type: "logout", icon: FiLogOut },
];

const PrivateRoute = ({ children }) => {
  const token = sessionStorage.getItem("token");
  return token ? children : <Home />;
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!sessionStorage.getItem("token"));

  // Update login state when location changes or on mount
  useEffect(() => {
    setIsLoggedIn(!!sessionStorage.getItem("token"));
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === "undefined") return;
      setIsMobile(window.innerWidth < 992);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleSidebarToggle = () => setIsSidebarOpen((prev) => !prev);
  const handleNavLinkClick = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Logout from TechXchange?",
      text: "You will be redirected to the home page.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#1e293b",
      confirmButtonText: "Logout",
    }).then((result) => {
      if (result.isConfirmed) {
        sessionStorage.clear();
        setIsLoggedIn(false);
        setIsSidebarOpen(false);
        // Navigate to home page
        navigate("/", { replace: true });
        // Small delay to ensure state updates, then reload for clean state
        setTimeout(() => {
          window.location.reload();
        }, 50);
      }
    });
  };

  const shouldShowBackdrop = isLoggedIn && isMobile && isSidebarOpen;
  const shellClassNames = [
    "app-shell",
    isLoggedIn ? "app-shell--dashboard" : "app-shell--public",
  ];

  if (shouldShowBackdrop) {
    shellClassNames.push("app-shell--sidebar-open");
  }

  return (
    <>
      <Authentication />
      <div className={shellClassNames.join(" ")}>
        {isLoggedIn && (
          <>
            <DashboardSidebar
              isOpen={isSidebarOpen}
              onLinkClick={handleNavLinkClick}
              onLogout={handleLogout}
            />
            <div
              className={`app-shell__backdrop ${shouldShowBackdrop ? "is-visible" : ""}`}
              onClick={handleSidebarToggle}
            />
          </>
        )}

        <div className="app-shell__content">
          <Header
            isLoggedIn={isLoggedIn}
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={isSidebarOpen}
            onLogout={handleLogout}
          />

          <main className={`app-main ${isLoggedIn ? "app-main--dashboard" : "app-main--public"}`}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route
                path="/addProduct"
                element={
                  <PrivateRoute>
                    <AddProduct />
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-products"
                element={
                  <PrivateRoute>
                    <MyProducts />
                  </PrivateRoute>
                }
              />
              <Route
                path="/categories"
                element={
                  <PrivateRoute>
                    <CategoryHub />
                  </PrivateRoute>
                }
              />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
              <Route
                path="/subcription"
                element={
                  <PrivateRoute>
                    <SubscriptionDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <PrivateRoute>
                    <ChatInbox />
                  </PrivateRoute>
                }
              />
              <Route
                path="/chat/:roomId"
                element={
                  <PrivateRoute>
                    <ChatRoom />
                  </PrivateRoute>
                }
              />
              <Route path="/category/:categoryId" element={<CategoryProducts />} />
            </Routes>
            
          </main>
        </div>
      </div>
    </>
  );
}

const DashboardSidebar = ({ isOpen, onLinkClick, onLogout }) => (
  <aside className={`app-sidebar ${isOpen ? "is-expanded" : "is-collapsed"}`}>
    <Link to="/" className="app-sidebar__brand" onClick={onLinkClick}>
      <img src={APP_LOGO} alt="TechXchange" />
    </Link>

    <nav className="app-sidebar__nav">
      {dashboardLinks.map((item) => {
        const Icon = item.icon;

        if (item.type === "logout") {
          return (
            <button
              key={item.id}
              type="button"
              className="app-sidebar__link  app-sidebar__link--logout"
              onClick={onLogout}
              data-tooltip="Dashboard"
            >
              <Icon />
              <span className="app-sidebar__label">{item.label}</span>
            </button>
          );
        }

        return (
          <NavLink
            key={item.id}
            to={item.path}
            className={({ isActive }) =>
              `app-sidebar__link ${isActive ? "is-active" : ""}`
            }
            onClick={onLinkClick}
          >
            <Icon />
            <span className="app-sidebar__label">{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  </aside>
);

export default App;
